import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import type {
  EstadoPagoFijo,
  PagoFijo,
  ResumenPagosFijos,
} from '#components/mobile/budget/pagosFijos';
import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import type { ExtraFila } from '#components/mobile/pagosGavi/filas';
import { useFormatosGavi } from '#components/mobile/pagosGavi/formatos';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Importe } from '#components/mobile/ui/Importe';
import { Pildora } from '#components/mobile/ui/Pildora';
import type { EstadoPildora } from '#components/mobile/ui/Pildora';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, TACTIL } from '#components/mobile/ui/tokens';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

/** Color de la píldora de cada estado. */
export const PILDORA_ESTADO: Record<EstadoPagoFijo, EstadoPildora> = {
  pagado: 'ok',
  'toca-hoy': 'aviso',
  'en-camino': 'aviso',
  atrasado: 'rojo',
  proximo: 'neutro',
  programado: 'ignorada',
};

export function useTextoEstadoPagoFijo() {
  const { t } = useTranslation();
  return (estado: EstadoPagoFijo): string =>
    ({
      pagado: t('Paid'),
      'toca-hoy': t('Due today'),
      // Domiciliado: los días de margen tras el cargo, «En camino».
      'en-camino': t('On its way'),
      atrasado: t('Late'),
      proximo: t('Upcoming'),
      programado: t('Later'),
    })[estado];
}

/**
 * Pestaña «Mensuales» de «Pagos fijos»: cabecera «Este mes: pagado X de Y»,
 * total por cuenta y una fila por pago ordenada por día de cargo.
 */
export function PagosFijosMensuales({
  mensuales,
  resumen,
  nombreCuenta,
  month,
  extras,
}: {
  mensuales: PagoFijo[];
  resumen: ResumenPagosFijos;
  nombreCuenta: (id: string) => string | undefined;
  month: string;
  /** Gavi (Altamar): cada comercio y la píldora «Sube», por categoría. */
  extras?: ReadonlyMap<string, ExtraFila | null>;
}) {
  const { t } = useTranslation();

  if (mensuales.length === 0) {
    return (
      <View style={{ padding: `0 ${espacio.margen}px` }}>
        <EstadoVacio
          ilustracion="barquito"
          titulo={t('No monthly fixed payments yet')}
          texto={t(
            'Open a category with a monthly target and turn on "Fixed payment".',
          )}
        />
      </View>
    );
  }

  const porCuenta = [...resumen.porCuenta.entries()].sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <View style={{ gap: espacio.tarjetas }} data-testid="pagos-fijos-mensuales">
      <Tarjeta style={{ margin: `0 ${espacio.margen}px` }}>
        <Text
          style={{ fontSize: 15, fontWeight: 700, color: color.fg }}
          data-testid="pagos-fijos-cabecera"
        >
          <Trans>This month: paid</Trans>{' '}
          <Importe valor={resumen.pagado} conEuro /> <Trans>of</Trans>{' '}
          <Importe valor={resumen.total} conEuro />
        </Text>
        <View
          style={{ marginTop: 8, gap: 4 }}
          data-testid="pagos-fijos-cuentas"
        >
          {porCuenta.map(([cuenta, total]: [string, IntegerAmount]) => (
            <View
              key={cuenta || 'sin-cuenta'}
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                fontSize: 13,
                color: color.fg2,
              }}
            >
              <Text>{(cuenta && nombreCuenta(cuenta)) || t('No account')}</Text>
              <Importe valor={total} conEuro />
            </View>
          ))}
        </View>
      </Tarjeta>

      <FilasPagosFijos
        pagos={mensuales}
        nombreCuenta={nombreCuenta}
        month={month}
        extras={extras}
      />
    </View>
  );
}

/** Texto «cada 2 meses» / «cada año» de una frecuencia de cobro. */
export function useTextoCadaMeses() {
  const { t } = useTranslation();
  return (meses: number): string =>
    meses === 12
      ? t('every year')
      : t('every {{count}} months', { count: Math.max(1, meses) });
}

/**
 * Filas de pagos fijos (día, nombre, cuenta, cada cuánto se cobra, importe
 * del cargo y estado). Las usan las pestañas «Mensuales» y «Anuales».
 */
export function FilasPagosFijos({
  pagos,
  nombreCuenta,
  month,
  extras,
}: {
  pagos: readonly PagoFijo[];
  nombreCuenta: (id: string) => string | undefined;
  month: string;
  extras?: ReadonlyMap<string, ExtraFila | null>;
}) {
  const { t } = useTranslation();
  const { euros } = useFormatosGavi();
  const navigate = useNavigate();
  const locale = useLocale();
  const textoEstado = useTextoEstadoPagoFijo();
  const textoCada = useTextoCadaMeses();
  return (
    <Tarjeta relleno={0} style={{ margin: `0 ${espacio.margen}px` }}>
      {pagos.map((p, i) => {
        const { emoji, resto } = separarEmoji(p.nombre);
        const extra = extras?.get(p.id) ?? null;
        const cuenta = p.todasLasCuentas
          ? t('All accounts')
          : p.cuentaId
            ? nombreCuenta(p.cuentaId)
            : undefined;
        return (
          <Button
            key={p.id}
            variant="bare"
            onPress={() => void navigate(rutaFicha(p.id, month))}
            data-testid="pago-fijo-fila"
            data-ayuda-fila={p.dia == null ? 'sin-dia' : 'normal'}
            style={{
              minHeight: TACTIL + 12,
              borderRadius: 0,
              borderTop: i === 0 ? 'none' : `1px solid ${color.line}`,
              padding: '8px 16px',
              justifyContent: 'flex-start',
              gap: 12,
              backgroundColor: 'transparent',
            }}
          >
            <Text
              data-ayuda="dia"
              style={{
                width: 32,
                textAlign: 'center',
                fontSize: 15,
                fontWeight: 800,
                color: color.fg2,
              }}
            >
              {p.dia ?? '–'}
            </Text>
            <View style={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: color.fg,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {emoji ? `${emoji} ${resto}` : resto}
              </Text>
              <Text
                data-ayuda="cuenta"
                style={{ fontSize: 12, color: color.fg3 }}
              >
                {[
                  p.dia == null
                    ? t('Set the charge day')
                    : (cuenta ?? t('No account')),
                  p.cadaMesesCobro != null && p.cadaMesesCobro > 1
                    ? textoCada(p.cadaMesesCobro)
                    : null,
                  p.tocaEsteMes === false && p.fecha
                    ? t('next: {{date}}', {
                        date: monthUtils.format(p.fecha, 'd MMM', locale),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {extra && extra.desglose.length > 0 && (
                <Text
                  data-testid="pago-fijo-desglose"
                  style={{
                    fontSize: 12,
                    color: color.fg2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {extra.desglose.map((d, j) => (
                    <Text key={d.clave}>
                      {j > 0 && ' · '}
                      {`${d.comercio}\u00a0${euros(d.importe)}`}
                      {d.sube != null && (
                        <Text style={{ color: color.warn, fontWeight: 800 }}>
                          {` ▲${euros(d.sube)}`}
                        </Text>
                      )}
                    </Text>
                  ))}
                </Text>
              )}
            </View>
            <View style={{ alignItems: 'flex-end', gap: 4 }}>
              {(p.importeCargo ?? p.importe) != null && (
                <span data-ayuda="importe">
                  <Importe valor={(p.importeCargo ?? p.importe)!} conEuro />
                </span>
              )}
              {extra?.sube != null && (
                <Pildora estado="aviso" data-testid="pago-fijo-sube">
                  {t('Up {{amount}}', { amount: euros(extra.sube) })}
                </Pildora>
              )}
              {extra?.interanual != null && (
                <Pildora estado="aviso" data-testid="pago-fijo-interanual">
                  {t('+{{percent}}\u00a0% on a year ago', {
                    percent: extra.interanual,
                  })}
                </Pildora>
              )}
              <Pildora
                estado={PILDORA_ESTADO[p.estado]}
                tamano="normal"
                data-testid={`pago-fijo-estado-${p.estado}`}
              >
                {textoEstado(p.estado)}
              </Pildora>
            </View>
          </Button>
        );
      })}
    </Tarjeta>
  );
}
